/**
 * RoboChat — Charlie, the header robot, as a guide.
 *
 * A scripted conversation, not an AI: every line Charlie says is written below,
 * and every fact in it is taken from this site's own pages. It asks for a
 * name, asks why the visitor is here and answers from a fixed set of topics.
 * When there is a project to discuss it asks what, when and where to reply,
 * shows the summary, and on a yes posts it to /api/lead, which emails the team.
 *
 * ── Why scripted ──────────────────────────────────────────────────────────
 * It cannot say anything untrue about the studio and it costs nothing to
 * run. To change what Charlie says, edit SCRIPT.
 *
 * The shell keeps this mounted, so the conversation survives navigation and
 * closing the panel. Only the visitor's first name is stored (localStorage),
 * so a returning visitor is greeted by name.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { brand } from './brand';
import { api } from './api';
import { spriteStyle } from './HeaderRobot';

const NAME_KEY = 'voilacode-robo-name';

type Ctx = { name: string; need: string; detail: string; timeline: string; email: string };

const EMPTY: Ctx = { name: '', need: '', detail: '', timeline: '', email: '' };

type Option = {
  label: string;
  /** Jump to another node. */
  next?: NodeId;
  /** Navigate to a page of this site, then carry on at `next` (default: 'after'). */
  to?: string;
  /** Open an external link (mailto). */
  href?: (c: Ctx) => string;
  /** Remember an answer. */
  set?: Partial<Ctx>;
  /** Email the collected project details to the team. */
  send?: boolean;
};

type Node = {
  say: (c: Ctx) => string[];
  options?: Option[];
  input?: { field: 'name' | 'detail' | 'email'; placeholder: string; next: NodeId; skippable?: boolean };
};

type NodeId =
  | 'hello' | 'welcome' | 'welcomeBack' | 'menu' | 'after'
  | 'project' | 'projectDetail' | 'timeline' | 'askEmail' | 'confirm' | 'sent' | 'sendFailed'
  | 'what' | 'engage' | 'work' | 'who' | 'browsing' | 'human';

const MAIN: Option[] = [
  { label: 'I have a project in mind', next: 'project' },
  { label: `What does ${brand.name} do?`, next: 'what' },
  { label: 'Show me your work', next: 'work' },
  { label: 'Who are you folks?', next: 'who' },
  { label: 'Just looking around', next: 'browsing' },
  { label: 'I want to talk to a person', next: 'human' },
];

/** One line about each thing we build, in the services page's own words. */
const NEEDS: Record<string, string> = {
  'Web app': 'Web apps are our bread and butter — React and Node.js products, from internal tools to customer-facing platforms.',
  'Mobile app': 'Nice. We build iOS and Android apps from one TypeScript codebase, with native capabilities where you need them.',
  'Desktop app': 'We build cross-platform desktop software for macOS, Windows and Linux that shares code with your web app.',
  'AI features': 'Good timing. We wire LLM features into real products: assistants, search, document processing and automation.',
  'SaaS platform': 'We build multi-tenant SaaS with tenancy, roles, billing and admin designed in from the first commit.',
  'Browser extension': 'We build Chrome and Firefox extensions that work alongside the tools your users already have open.',
  'Not sure yet': 'That is completely fine — most good projects start as a rough idea. Working out the shape of it is part of what we do.',
};

function mailto(c: Ctx): string {
  const subject = c.need ? `Project enquiry — ${c.need}` : `Hello from ${c.name || 'your website'}`;
  const lines = [
    `Hi ${brand.name} team,`,
    '',
    c.name ? `I'm ${c.name}.` : '',
    c.need ? `I'm interested in: ${c.need}.` : '',
    c.detail,
    '',
    'Thanks,',
    c.name,
  ].filter((l, i, all) => l !== '' || all[i - 1] !== '');
  return `mailto:${brand.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
}

const SCRIPT: Record<NodeId, Node> = {
  hello: {
    say: () => [
      `Hi there! 👋 I'm Charlie. I live up here on the header and write code all day.`,
      `I'd love to help you find your way around. First things first — what should I call you?`,
    ],
    input: { field: 'name', placeholder: 'Your first name', next: 'welcome' },
  },
  welcome: {
    say: (c) => [
      `Lovely to meet you, ${c.name}! 😊`,
      `No pressure and no sales pitch here. Tell me what brought you by and I'll point you the right way.`,
      `So — why are you here today?`,
    ],
    options: MAIN,
  },
  welcomeBack: {
    say: (c) => [`Welcome back, ${c.name}! 👋 Good to see you again.`, `What can I help you with today?`],
    options: MAIN,
  },
  menu: {
    say: (c) => [`Sure thing, ${c.name}. What else can I help with?`],
    options: MAIN,
  },
  after: {
    say: () => [`There you go! Take your time — I'll stay right here if you need anything else.`],
    options: MAIN,
  },

  project: {
    say: (c) => [
      `Exciting! I like the sound of that already, ${c.name}.`,
      `What kind of thing are you building? Pick the closest one — it's fine if it's a mix.`,
    ],
    options: Object.keys(NEEDS).map((need) => ({ label: need, set: { need }, next: 'projectDetail' as NodeId })),
  },
  projectDetail: {
    say: (c) => [
      NEEDS[c.need] ?? NEEDS['Not sure yet'],
      `Want to tell me a little more? One line is plenty — or skip it, there are no wrong answers.`,
    ],
    input: { field: 'detail', placeholder: 'e.g. a booking app for clinics', next: 'timeline', skippable: true },
  },
  timeline: {
    say: (c) => [
      c.detail ? `Got it — that helps a lot.` : `No problem at all.`,
      `Roughly when would you like to get started?`,
    ],
    options: ['As soon as possible', 'In 1–3 months', 'In 3–6 months', 'Just exploring for now'].map(
      (timeline) => ({ label: timeline, set: { timeline }, next: 'askEmail' as NodeId }),
    ),
  },
  askEmail: {
    say: (c) => [
      `Almost done, ${c.name} — last question, I promise.`,
      `What's the best email for the team to reply to? It's only used to answer you.`,
    ],
    input: { field: 'email', placeholder: 'you@company.com', next: 'confirm' },
  },
  confirm: {
    say: (c) => [
      `Here's what I've got:`,
      [
        `• Name — ${c.name}`,
        `• Building — ${c.need}`,
        c.detail ? `• Details — ${c.detail}` : '',
        `• Timeline — ${c.timeline}`,
        `• Reply to — ${c.email}`,
      ].filter(Boolean).join('\n'),
      `Shall I send this to the team?`,
    ],
    options: [
      { label: 'Yes, send it ✓', send: true },
      { label: 'Change something', next: 'project' },
      { label: 'Not right now', next: 'menu' },
    ],
  },
  sent: {
    say: (c) => [
      `Sent! 🎉 Thank you, ${c.name}.`,
      `The team has your note and will reply to ${c.email}. Feel free to keep looking around — I'm right here if you need me.`,
    ],
    options: MAIN.filter((o) => o.next !== 'project'),
  },
  sendFailed: {
    say: (c) => [
      `Hmm, that didn't go through on my side — sorry about that, ${c.name}.`,
      `You can try again, or email the team directly at ${brand.email}.`,
    ],
    options: [
      { label: 'Try again', send: true },
      { label: 'Write an email ✉', href: mailto, next: 'after' },
      { label: 'Something else', next: 'menu' },
    ],
  },

  what: {
    say: () => [
      `Happy to explain! ${brand.name} is a software consulting studio in Hyderabad, India.`,
      `We build production apps across web, desktop and mobile — plus AI features, SaaS platforms and browser extensions — working as an extension of your team.`,
      `We build with AI coding agents, and an engineer reads, tests and approves every change before it ships.`,
    ],
    options: [
      { label: 'How do you work with teams?', next: 'engage' },
      { label: 'Take me to Services', to: '/services' },
      { label: 'I have a project in mind', next: 'project' },
      { label: 'Something else', next: 'menu' },
    ],
  },
  engage: {
    say: () => [
      `Three ways, depending on what you need:`,
      `• Individual engineers — full-stack TypeScript engineers working day to day with your team.\n• Embedded squad — a small team with a lead who owns a product area end to end.\n• Agent enablement — we set up coding agents in your codebase and train your team to use them.`,
      `Either way: short build cycles, regular demos on working software, and no guesswork.`,
    ],
    options: [
      { label: 'I have a project in mind', next: 'project' },
      { label: 'Take me to Services', to: '/services' },
      { label: 'Something else', next: 'menu' },
    ],
  },
  work: {
    say: () => [
      `Good call — seeing is better than hearing about it.`,
      `The Work page shows products we've engineered across SaaS, desktop, mobile and AI, each one running for real users today.`,
    ],
    options: [
      { label: 'Take me to Work', to: '/work' },
      { label: 'I have a project in mind', next: 'project' },
      { label: 'Something else', next: 'menu' },
    ],
  },
  who: {
    say: () => [
      `We're ${brand.legalName} — founded in 2023 in Hitech City, Hyderabad, and working with teams around the world.`,
      `A few things people like to know up front: we're NDA-ready and GDPR-aligned, a DPA is available on request, and the IP is assigned to you at delivery.`,
    ],
    options: [
      { label: 'Take me to About', to: '/about' },
      { label: `What does ${brand.name} do?`, next: 'what' },
      { label: 'Something else', next: 'menu' },
    ],
  },
  browsing: {
    say: (c) => [
      `That's completely fine, ${c.name} — look around as long as you like. I'll be right here on the header. 🙂`,
      `If you'd like a starting point, these are the popular ones:`,
    ],
    options: [
      { label: 'Services', to: '/services' },
      { label: 'Work', to: '/work' },
      { label: 'About', to: '/about' },
      { label: 'Actually, I have a question', next: 'menu' },
    ],
  },
  human: {
    say: () => [
      `Of course — sometimes you just want a person.`,
      `You can reach the founder directly at ${brand.email}, in ${brand.address}.`,
    ],
    options: [
      { label: 'Write an email ✉', href: mailto, next: 'after' },
      { label: 'Open the Contact page', to: '/contact' },
      { label: 'Something else', next: 'menu' },
    ],
  },
};

/** `read` turns a sent message's ticks blue, the moment Charlie "opens" it. */
type Message = { from: 'robo' | 'user'; text: string; at: Date; read?: boolean };

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
const clock = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** WhatsApp's double tick. */
function Ticks({ read }: { read?: boolean }) {
  return (
    <svg viewBox="0 0 18 11" aria-label={read ? 'Read' : 'Delivered'} className={`inline-block h-[10px] w-[16px] ${read ? 'text-[#7FD4FF]' : 'text-white/60'}`}>
      <path d="M1 6l3 3 6-8M7 8.5l1 .5 6-8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function readName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

export function RoboChat({
  open, onClose, onSpeak,
}: {
  open: boolean;
  onClose: () => void;
  /** Called each time Charlie says something, so the header robot can look up. */
  onSpeak?: () => void;
}) {
  const navigate = useNavigate();
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [typing, setTyping] = React.useState(false);
  const [node, setNode] = React.useState<NodeId | null>(null);
  const [draft, setDraft] = React.useState('');
  const [invalid, setInvalid] = React.useState(false);
  const ctx = React.useRef<Ctx>({ ...EMPTY });
  const run = React.useRef(0); // bumped to cancel a conversation in flight
  const scroller = React.useRef<HTMLDivElement>(null);
  const started = React.useRef(false);

  /*
   * Say a node's lines the way a person texts: a beat to "read" what was sent
   * (the ticks go blue), then "typing…" for about as long as the line would
   * take to type, the message, and a short pause before the next one.
   */
  const go = React.useCallback(async (id: NodeId) => {
    const mine = ++run.current;
    setNode(null);
    await wait(450);
    if (run.current !== mine) return;
    setMessages((all) => all.map((m) => (m.from === 'user' ? { ...m, read: true } : m)));
    for (const text of SCRIPT[id].say(ctx.current)) {
      await wait(250);
      if (run.current !== mine) return;
      setTyping(true);
      await wait(Math.min(600 + text.length * 14, 2200));
      if (run.current !== mine) return;
      setTyping(false);
      setMessages((all) => [...all, { from: 'robo', text, at: new Date() }]);
      onSpeak?.();
    }
    setNode(id);
  }, [onSpeak]);

  // Start on first open, not on mount: the greeting should play while they watch.
  React.useEffect(() => {
    if (!open || started.current) return;
    started.current = true;
    ctx.current.name = readName();
    void go(ctx.current.name ? 'welcomeBack' : 'hello');
  }, [open, go]);

  React.useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [messages, typing, node]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const userSays = (text: string) => setMessages((all) => [...all, { from: 'user', text, at: new Date() }]);

  async function send() {
    const mine = ++run.current;
    setNode(null);
    setTyping(true);
    let ok = true;
    try {
      await api.post('/api/lead', { ...ctx.current, page: window.location.pathname });
    } catch {
      ok = false;
    }
    if (run.current !== mine) return;
    void go(ok ? 'sent' : 'sendFailed');
  }

  function choose(option: Option) {
    userSays(option.label);
    if (option.set) Object.assign(ctx.current, option.set);
    if (option.send) return void send();
    if (option.to) navigate(option.to);
    if (option.href) window.location.href = option.href(ctx.current);
    void go(option.next ?? 'after');
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const input = node ? SCRIPT[node].input : undefined;
    if (!input) return;
    const value = draft.trim().slice(0, input.field === 'name' ? 40 : 300);
    if (!value && !input.skippable) return;
    if (input.field === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setDraft('');
    if (input.field === 'name') {
      ctx.current.name = value;
      try { localStorage.setItem(NAME_KEY, value); } catch { /* still works for this visit */ }
    } else {
      ctx.current[input.field] = value;
    }
    userSays(value || 'Skip');
    void go(input.next);
  }

  function startOver() {
    try { localStorage.removeItem(NAME_KEY); } catch { /* nothing stored, nothing to clear */ }
    ctx.current = { ...EMPTY };
    setMessages([]);
    setDraft('');
    void go('hello');
  }

  if (!open) return null;
  const current = node ? SCRIPT[node] : null;

  return (
    <div
      role="dialog"
      aria-label="Chat with Charlie"
      className="fixed left-[clamp(12px,4vw,64px)] top-[78px] z-40 flex max-h-[min(580px,calc(100dvh-96px))] w-[min(380px,calc(100vw-24px))] flex-col overflow-hidden rounded-2xl border border-line-strong bg-card text-card-foreground shadow-[0_24px_60px_-12px_rgb(0_0_0/.55)]"
    >
      <div className="flex items-center gap-3 border-b border-line px-4 py-3">
        <span aria-hidden className="size-9 shrink-0 overflow-hidden rounded-full bg-brand/15">
          {/* The face-on frame, zoomed to the head. */}
          <span className="block h-[50px] w-9" style={spriteStyle(45)} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold leading-tight">Charlie</span>
          {/* WhatsApp-style presence: "typing…" while a reply is on its way. */}
          <span className="flex items-center gap-1.5 truncate text-xs" aria-live="polite">
            {typing ? (
              <span className="text-primary">typing…</span>
            ) : (
              <>
                <span aria-hidden className="size-1.5 rounded-full bg-[#25D366]" />
                <span className="text-muted-foreground">online · {brand.name}'s coding buddy</span>
              </>
            )}
          </span>
        </span>
        <button
          type="button"
          onClick={startOver}
          className="cursor-pointer rounded-full px-2 py-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
        >
          Restart
        </button>
        <button
          type="button"
          aria-label="Close chat"
          onClick={onClose}
          className="grid size-8 cursor-pointer place-items-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
        >
          ✕
        </button>
      </div>

      <div
        ref={scroller}
        aria-live="polite"
        className="flex flex-1 flex-col gap-1 overflow-y-auto bg-[radial-gradient(var(--color-line)_1px,transparent_1px)] bg-[size:18px_18px] px-3 py-4 text-sm leading-relaxed"
      >
        {messages.map((m, i) => {
          const mine = m.from === 'user';
          // The first bubble in a run gets the tail and a little more space above.
          const first = i === 0 || messages[i - 1].from !== m.from;
          return (
            <div
              key={i}
              className={[
                'relative max-w-[85%] whitespace-pre-line px-3 pb-1.5 pt-2 shadow-[0_1px_1px_rgb(0_0_0/.12)]',
                'transition-all duration-200 ease-out starting:translate-y-2 starting:scale-95 starting:opacity-0',
                mine
                  ? 'self-end rounded-xl bg-brand text-white'
                  : 'self-start rounded-xl bg-popover text-popover-foreground',
                first ? (mine ? 'mt-2 rounded-tr-none' : 'mt-2 rounded-tl-none') : '',
              ].join(' ')}
            >
              {first && (
                <svg
                  aria-hidden
                  viewBox="0 0 8 13"
                  className={`absolute top-0 h-[13px] w-2 ${mine ? '-right-2 fill-brand' : '-left-2 -scale-x-100 fill-popover'}`}
                >
                  <path d="M0 0h8L1 12.5C.4 13 0 12.6 0 12V0z" />
                </svg>
              )}
              {m.text}
              {/* Time (and ticks) tucked into the bubble's corner. */}
              <span className={`float-right ml-3 mt-1.5 inline-flex translate-y-0.5 items-center gap-1 text-[10px] ${mine ? 'text-white/70' : 'text-muted-foreground'}`}>
                {clock(m.at)}
                {mine && <Ticks read={m.read} />}
              </span>
            </div>
          );
        })}

        {typing && (
          <div aria-label="Charlie is typing" className="mt-2 flex gap-1 self-start rounded-xl rounded-tl-none bg-popover px-3.5 py-3 shadow-[0_1px_1px_rgb(0_0_0/.12)]">
            {[0, 150, 300].map((delay) => (
              <span
                key={delay}
                className="size-1.5 animate-bounce rounded-full bg-muted-foreground"
                style={{ animationDelay: `${delay}ms` }}
              />
            ))}
          </div>
        )}

        {current?.options && (
          <div className="mt-3 flex flex-wrap gap-2">
            {current.options.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => choose(option)}
                className="cursor-pointer rounded-full border border-line-strong bg-card px-3 py-1.5 text-left text-[13px] transition-colors hover:border-primary hover:text-primary"
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {current?.input && (
        <form onSubmit={submit} noValidate className="flex flex-wrap items-center gap-2 border-t border-line p-3">
          {invalid && (
            <span role="alert" className="w-full px-1 text-xs text-destructive">
              That doesn't look like an email address — mind checking it?
            </span>
          )}
          <input
            autoFocus
            value={draft}
            type={current.input.field === 'email' ? 'email' : 'text'}
            autoComplete={current.input.field === 'email' ? 'email' : current.input.field === 'name' ? 'given-name' : 'off'}
            aria-invalid={invalid}
            onChange={(e) => { setDraft(e.target.value); setInvalid(false); }}
            placeholder={current.input.placeholder}
            aria-label={current.input.placeholder}
            maxLength={current.input.field === 'name' ? 40 : 300}
            // 16px on touch screens: iOS zooms the whole page into any field under 16px
            // when it is tapped, which left the chat oversized until the visitor zoomed out.
            className="h-10 min-w-0 flex-1 rounded-full border border-line-strong bg-transparent px-4 text-base [@media(pointer:fine)]:text-sm outline-none transition-colors placeholder:text-subtle focus:border-primary aria-[invalid=true]:border-destructive"
          />
          <button
            type="submit"
            className="h-10 cursor-pointer rounded-full bg-brand px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-hover"
          >
            {current.input.skippable && !draft.trim() ? 'Skip' : 'Send'}
          </button>
        </form>
      )}
    </div>
  );
}
